import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useHistory } from 'react-router-dom';
import { Layout, Breadcrumb, Avatar } from 'antd';
import { makeStyles } from '@material-ui/core';
import { Route } from 'antd/lib/breadcrumb/Breadcrumb';
import { useGetBox } from 'src/reactQuery/box/useGetBox';
import {
  BoxData,
  BoxTimestamp,
  isBoxRead,
  isSurveyResult,
  isSurveyResultBoxType,
  SurveyResultView,
  toSurveyResultView
} from 'src/reactQuery/box/interfaces/box.interface';
import { useMarkBoxRead } from 'src/reactQuery/box/useMarkBoxRead';
import { isCurrentProjectCitizen } from 'src/util/projectCheckers';
import ArrowNextIcon from '../../../asset/arrow-next.svg';
import CButton from '../../../component/Button';
import Loader from '../../../component/Loader/Loader';

const pad = (value: number) => value.toString().padStart(2, '0');

/** Box timestamps are not consistently typed across box variants: ISO strings
 *  ("2026-07-17T11:21:34.148889Z"), space-separated ones ("2024-10-23 12:01:24.248858")
 *  that Safari refuses to parse, already-localised unparseable strings ("2 მაი, 2024"),
 *  epoch numbers, and Date instances all occur. Accepts anything and never throws — a
 *  timestamp must not be able to take down the page. Not using the shared formatDate
 *  helper: it emits YYYY/DD/MM. */
const formatBoxDate = (value?: BoxTimestamp | null): string => {
  if (!value) return '-';

  let parsed: Date;

  if (value instanceof Date) {
    parsed = value;
  } else if (typeof value === 'number') {
    parsed = new Date(value);
  } else if (typeof value === 'string') {
    parsed = new Date(value.replace(' ', 'T'));
  } else {
    return '-';
  }

  if (Number.isNaN(parsed.getTime())) return typeof value === 'string' ? value : '-';

  const date = [parsed.getFullYear(), pad(parsed.getMonth() + 1), pad(parsed.getDate())].join('/');

  return `${date} ${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
};

const useStyles = makeStyles({
  gridContainer: {
    display: 'grid',
    gridRowGap: '25px',
    gridTemplateColumns: '33% 33% 33%',
    marginLeft: '90px',
    marginTop: '55px'
  },
  gridHeader: {
    gridColumn: '33% 33% 33%',
    gridRow: '1',
    textAlign: 'start'
  },
  gridItem: {
    maxWidth: '90%',
    textAlign: 'start'
  },
  gridItemPayment: {
    display: 'flex',
    flexDirection: 'column',
    gridColumn: '3',
    gridRow: '1 / span 2',
    height: '100%',
    justifyContent: 'space-between',
    maxWidth: '90%',
    textAlign: 'start'
  },
  gridItemSelect: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    maxWidth: '90%',
    textAlign: 'start'
  },
  p: {
    color: '#9BA0AD',
    fontFamily: 'Poppins',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 'normal',
    lineHeight: '100%',
    marginTop: '12px'
  },
  probableAnswers: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: '1fr 1fr'
  },
  selectHeader: {
    fontFamily: 'Poppins',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 500,
    margin: '0px'
  },
  span: {
    fontFamily: 'Poppins',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 500
  },
  surveyAnswer: {
    background: '#F5F7FF',
    borderLeft: '3px solid #3A57E8',
    borderRadius: '8px',
    color: 'black',
    fontFamily: 'Poppins',
    fontSize: '18px',
    fontWeight: 500,
    lineHeight: 1.5,
    padding: '14px 18px'
  },
  surveyLabel: {
    color: '#9BA0AD',
    fontFamily: 'Poppins',
    fontSize: '12px',
    letterSpacing: '0.04em',
    lineHeight: 1.4,
    margin: '0px 0px 8px 0px',
    textTransform: 'uppercase'
  },
  surveyMeta: {
    borderTop: '1px solid #EEF1F8',
    display: 'flex',
    gap: '56px',
    paddingTop: '20px'
  },
  surveyOption: {
    alignItems: 'center',
    border: '1px solid #EEF1F8',
    borderRadius: '8px',
    color: '#5A6076',
    display: 'flex',
    fontFamily: 'Poppins',
    fontSize: '14px',
    gap: '12px',
    lineHeight: 1.5,
    padding: '12px 16px'
  },
  surveyOptionIndex: {
    alignItems: 'center',
    background: '#F4F6FA',
    borderRadius: '50%',
    color: '#9BA0AD',
    display: 'flex',
    flexShrink: 0,
    fontSize: '12px',
    height: '24px',
    justifyContent: 'center',
    width: '24px'
  },
  surveyOptionSelected: {
    '& $surveyOptionIndex': {
      background: '#3A57E8',
      color: 'white'
    },
    background: '#F5F7FF',
    borderColor: '#3A57E8',
    color: 'black',
    fontWeight: 500
  },
  surveyOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  surveyQuestion: {
    color: 'black',
    fontFamily: 'Poppins',
    fontSize: '22px',
    fontWeight: 500,
    lineHeight: 1.45,
    margin: '0px'
  },
  surveyRoot: {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
    marginTop: '40px',
    maxWidth: '720px'
  },
  surveyValue: {
    color: 'black',
    fontFamily: 'Poppins',
    fontSize: '14px',
    lineHeight: 1.5
  }
});

const BoxDetail = () => {
  const history = useHistory();
  // @ts-ignore
  const { boxId } = useParams();
  const classes = useStyles();
  const { data: selectedBox, isLoading } = useGetBox(boxId);
  const { mutate: markBoxRead } = useMarkBoxRead();
  const markedReadFor = useRef<string | undefined>(undefined);

  const [isEditModeOn, setIsEditModeOn] = useState<boolean>(false);

  /** Opening a Residents Portal survey submission is what turns it from "Unread"
   *  to "Read" in the Box list. Guarded by a ref so the refetch that follows the
   *  update does not re-trigger it. */
  useEffect(() => {
    if (!isCurrentProjectCitizen || !selectedBox) return;
    if (!isSurveyResultBoxType(selectedBox.type) || isBoxRead(selectedBox)) return;
    if (markedReadFor.current === selectedBox._id) return;

    markedReadFor.current = selectedBox._id;
    markBoxRead(selectedBox);
  }, [selectedBox]);

  function itemRender(route: Route, params: any, routes: Route[]) {
    const last = routes.indexOf(route) === routes.length - 1;

    return last ? (
      <span>{route.breadcrumbName}</span>
    ) : (
      <Link to={route.path}>{route.breadcrumbName}</Link>
    );
  }

  const routes: Route[] = useMemo(() => {
    return [
      {
        breadcrumbName: 'Boxes',
        path: '/box'
      },
      {
        breadcrumbName: selectedBox?._id || '',
        path: `/box/${1}`
      }
    ];
  }, [selectedBox]);

  const onUpdate = () => {
    history.push('/box');
  };

  const SupplierHeader = () => {
    // need profilePictureUrl
    const metadata = selectedBox?.metadata;
    const userName = isCurrentProjectCitizen
      ? selectedBox?.fullName || metadata?.fullName
      : undefined;
    const userIdentity = userName || selectedBox?.user;
    return (
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          gap: '16px'
        }}
      >
        {!!metadata?.image && (
          <Avatar
            src={metadata.image}
            style={{
              flexShrink: 0,
              height: '55px',
              width: '55px'
            }}
          />
        )}
        {userIdentity && (
          <p className={classes.p} style={{ marginTop: '0px' }}>
            User: <span style={{ color: 'black' }}>{userIdentity}</span>
          </p>
        )}
      </div>
    );
  };

  const SurveyResultBody = ({ result }: { result: SurveyResultView }) => (
    <div className={classes.surveyRoot}>
      <div>
        <p className={classes.surveyLabel}>{result.surveyName || 'Survey'}</p>
        <h2 className={classes.surveyQuestion}>{result.question || '-'}</h2>
      </div>

      <div>
        <p className={classes.surveyLabel}>Answer</p>
        <div className={classes.surveyAnswer}>{result.answer || '-'}</div>
      </div>

      {!!result.options?.length && (
        <div>
          <p className={classes.surveyLabel}>All options</p>
          <div className={classes.surveyOptions}>
            {result.options.map((option, index) => {
              const isSelected = index === result.selectedIndex;

              return (
                <div
                  key={index}
                  className={`${classes.surveyOption} ${
                    isSelected ? classes.surveyOptionSelected : ''
                  }`}
                >
                  <span className={classes.surveyOptionIndex}>{index + 1}</span>
                  <span style={{ flex: 1 }}>{option}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className={classes.surveyMeta}>
        <div>
          <p className={classes.surveyLabel}>Answered at</p>
          <div className={classes.surveyValue}>{formatBoxDate(result.answeredAt)}</div>
        </div>
        {result.locale && (
          <div>
            <p className={classes.surveyLabel}>Locale</p>
            <div className={classes.surveyValue}>{result.locale}</div>
          </div>
        )}
      </div>
    </div>
  );

  const OrderBody = () => {
    const data = selectedBox?.data;
    if (isSurveyResult(data)) {
      return <SurveyResultBody result={toSurveyResultView(data)} />;
    }

    const questions = Array.isArray(data) ? data : [];
    return (
      <div className={classes.gridContainer}>
        {questions.map((question: BoxData, index) => {
          return (
            <div key={index} className={classes.gridItem}>
              <p>{question.questionDescription || '-'}</p>
              <div className={classes.gridItem}>
                <p className={classes.p}>Probable answers</p>
                <div className={classes.probableAnswers}>
                  {Object.values(question.probableAnswers || {}).map((probableAnswer, i) => {
                    return <p key={i}>{probableAnswer}</p>;
                  })}
                </div>
              </div>
              <div className={classes.gridItem}>
                <p className={classes.p}>Correct answer</p>
                <div>{question.correctAnswer}</div>
              </div>
              <div className={classes.gridItem}>
                <p className={classes.p}>Selected answer</p>
                <div>{question.selectedAnswer}</div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Layout style={{ background: 'white' }}>
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          padding: '35px 40px 0px 35px'
        }}
      >
        <Breadcrumb
          itemRender={itemRender}
          routes={routes}
          separator={<img alt="separator icon" src={ArrowNextIcon} />}
          style={{
            background: 'white',
            display: 'flex',
            justifyContent: 'flex-start'
          }}
        />
        <div>
          <CButton onClick={onUpdate} type="primary">
            Save
          </CButton>
          <CButton onClick={() => setIsEditModeOn(!isEditModeOn)} type="primary">
            {isEditModeOn ? 'Cancel' : 'Edit'}
          </CButton>
        </div>
      </div>
      <div style={{ marginTop: '55px', padding: '0px 40px 0px 35px' }}>
        <SupplierHeader />
        <OrderBody />
      </div>
    </Layout>
  );
};

export default BoxDetail;
